import * as React from "react";
import { useState, useCallback } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import translations from "translations";
import { useRecoilValue, useSetRecoilState, atom, RecoilState } from "recoil";
import { useHasFileVisibilityPermission, useIsCertificateEditMode } from "lib/userInfo";
import { TenantRole } from "graphqlBase/types";
import UnitParameterVisibilityModal from "components/formElements/fields/UnitParameterVisibilityModal";
import Props from "./index.d";

const UnitParameterVisibility: React.FC<Props> = (props) => {
  const { getFieldValue, isEdit } = props;

  // Create dummy atoms FIRST before use
  const dummyRolesAtom = React.useMemo(
    () =>
      atom<TenantRole[] | undefined>({
        key: `unitParams-roles-dummy-${Math.random()}`,
        default: undefined,
      }),
    []
  );

  const dummyTenantIdsAtom = React.useMemo(
    () =>
      atom<string[] | undefined>({
        key: `unitParams-tenantIds-dummy-${Math.random()}`,
        default: undefined,
      }),
    []
  );

  // Permission and visibility modal state
  // Requires: TenantAdmin OR AccessRequestManager (user role)
  //       AND MANUFACTURER, CERTIFICATE_AUTHORITY, or PLATFORM_OPERATOR (tenant role)
  const hasFileVisibilityPermission = useHasFileVisibilityPermission();
  // Check if we're in edit mode - visibility controls should only show during upload
  const isCertificateEditMode = useIsCertificateEditMode();
  const [openVisibilityModal, setOpenVisibilityModal] = useState(false);

  // Get grant fields from form context
  const grantAccessToUnitParametersRolesSelector = React.useMemo(() => {
    try {
      return getFieldValue("grantAccessToUnitParametersRoles");
    } catch (error) {
      return null;
    }
  }, [getFieldValue]);

  const grantAccessToUnitParametersTenantIdsSelector = React.useMemo(() => {
    try {
      return getFieldValue("grantAccessToUnitParametersTenantIds");
    } catch (error) {
      return null;
    }
  }, [getFieldValue]);

  const currentRoles = useRecoilValue(grantAccessToUnitParametersRolesSelector || dummyRolesAtom);
  const currentTenantIds = useRecoilValue(grantAccessToUnitParametersTenantIdsSelector || dummyTenantIdsAtom);

  // Get setters for updating grant values
  const setRoles = useSetRecoilState(
    (grantAccessToUnitParametersRolesSelector || dummyRolesAtom) as RecoilState<TenantRole[] | undefined>
  );
  const setTenantIds = useSetRecoilState(
    (grantAccessToUnitParametersTenantIdsSelector || dummyTenantIdsAtom) as RecoilState<string[] | undefined>
  );

  // Visibility modal handlers
  const hasVisibilityGrants = useCallback(() => {
    const roles = currentRoles as TenantRole[] | undefined;
    const tenantIds = currentTenantIds as string[] | undefined;
    return (roles && roles.length > 0) || (tenantIds && tenantIds.length > 0);
  }, [currentRoles, currentTenantIds]);

  const getVisibilityTooltip = useCallback(() => {
    const roles = currentRoles as TenantRole[] | undefined;
    const tenantIds = currentTenantIds as string[] | undefined;

    if (!roles && !tenantIds) {
      return translations.pages.certificateUpload.unitVisibility.configureAccess;
    }

    const hasGrants = (roles && roles.length > 0) || (tenantIds && tenantIds.length > 0);
    if (!hasGrants) {
      return translations.pages.certificateUpload.unitVisibility.configureAccess;
    }

    const grantSummary: string[] = [];
    if (roles && roles.length > 0) {
      grantSummary.push(
        `${translations.pages.certificateUpload.unitVisibility.grantAccessToRoles}: ${roles.join(", ")}`
      );
    }
    if (tenantIds && tenantIds.length > 0) {
      grantSummary.push(
        `${translations.pages.certificateUpload.unitVisibility.grantAccessToTenants}: ${tenantIds.length} tenant(s)`
      );
    }
    return grantSummary.join(" | ");
  }, [currentRoles, currentTenantIds]);

  const handleVisibilitySave = useCallback(
    (roles: TenantRole[], tenantIds: string[]) => {
      // Update form state with new grants
      setRoles(roles);
      setTenantIds(tenantIds);
      setOpenVisibilityModal(false);
    },
    [setRoles, setTenantIds]
  );

  // Hide visibility controls in edit mode - only show during upload
  if (!hasFileVisibilityPermission || isEdit || isCertificateEditMode) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1, width: "100%", pl: "32px", pt: 1 }}>
      <Typography sx={{ minWidth: "200px", fontSize: "18px", fontWeight: 400, color: "text.secondary" }}>
        {translations.pages.certificateUpload.unitVisibility.configureAccess}:
      </Typography>
      <Tooltip title={getVisibilityTooltip()}>
        <IconButton disableRipple onClick={() => setOpenVisibilityModal(true)} size="small">
          {hasVisibilityGrants() ? <VisibilityIcon sx={{ color: "primary.main" }} /> : <VisibilityOffIcon />}
        </IconButton>
      </Tooltip>

      {/* Unit Parameter Visibility Modal */}
      <UnitParameterVisibilityModal
        open={openVisibilityModal}
        currentRoles={currentRoles as TenantRole[] | undefined}
        currentTenantIds={currentTenantIds as string[] | undefined}
        onClose={() => setOpenVisibilityModal(false)}
        onSave={handleVisibilitySave}
      />
    </Box>
  );
};

export default UnitParameterVisibility;
